import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import baseUrl from "../api/utils";

const RestorePage = () => {
  const navigate = useNavigate();
  const MySwal = withReactContent(Swal);
  const [backups, setBackups] = useState([]);
  const [selectedBackup, setSelectedBackup] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = sessionStorage.getItem("token");

  // ✅ NEW: same pattern as LicenceDetails.jsx (Activeprofile / SAS check)
  const [activeProfile, setActiveProfile] = useState(
    sessionStorage.getItem("Activeprofile") || ""
  );
  const isSaas = activeProfile === "SAS";

  const fetchActiveEnvironment = async () => {
    // If some other page already fetched it and stored it, skip the call.
    const cached = sessionStorage.getItem("Activeprofile");
    if (cached) {
      setActiveProfile(cached);
      return;
    }
    try {
      const active = await axios.get(`${baseUrl}/Active/Environment`);
      if (active?.data?.environment) {
        sessionStorage.setItem("Activeprofile", active.data.environment);
        setActiveProfile(active.data.environment);
      }
    } catch (error) {
      console.error("Error fetching active environment", error);
    }
  };

  const fetchBackups = async () => {
    try {
      const res = await axios.get(`${baseUrl}/listbackups`, {
        headers: {
          Authorization: token,
        },
      }); // replace with your backend endpoint
      console.log(res.data);
      setBackups(res.data || []);
    } catch (err) {
      if (err?.res?.status === 401) {
        navigate("/unauthorized");
      }
      console.error("Error fetching backups", err);
    }
  };

  useEffect(() => {
    fetchActiveEnvironment();
    fetchBackups();
  }, []);

  const handleServerRestore = async () => {
    if (!selectedBackup) {
      MySwal.fire("⚠️ Warning", "Please select a backup file.", "warning");
      return;
    }

    // ✅ NEW: extra confirmation step in SaaS mode, since this restores
    // ALL companies' data together, not just one.
    if (isSaas) {
      const confirm = await MySwal.fire({
        title: "⚠️ Restore ALL companies?",
        html: "This is a <b>full platform restore</b>. Every company's data will be reverted to this backup's date, not just one company.<br/><br/>Type <b>RESTORE</b> to confirm.",
        input: "text",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm Restore",
        confirmButtonColor: "#d33",
        inputValidator: (value) => {
          if (value !== "RESTORE") {
            return 'You must type "RESTORE" exactly to proceed.';
          }
        },
      });
      if (!confirm.isConfirmed) {
        return;
      }
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}/backups/restore/server`,
        null, // POST body is empty
        {
          headers: {
            Authorization: token,
          },
          params: {
            fileName: selectedBackup, // ✅ send as request param
          },
        }
      );

      MySwal.fire("✅ Success", response?.data || "Restore completed successfully!", "success");
    } catch (error) {
      const errorMsg = error.response?.data || "❌ Error restoring from server.";
      MySwal.fire("❌ Restore Failed", errorMsg, "error");
      console.error("Server restore error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadRestore = async () => {
    try {
      if (!uploadFile) {
        MySwal.fire("⚠️ Warning", "Please upload a .zip file.", "warning");
        return;
      }

      // ✅ NEW: same confirmation step for the upload-restore path in SaaS mode
      if (isSaas) {
        const confirm = await MySwal.fire({
          title: "⚠️ Restore ALL companies?",
          html: "This is a <b>full platform restore</b>. Every company's data will be reverted to this backup's date, not just one company.<br/><br/>Type <b>RESTORE</b> to confirm.",
          input: "text",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Confirm Restore",
          confirmButtonColor: "#d33",
          inputValidator: (value) => {
            if (value !== "RESTORE") {
              return 'You must type "RESTORE" exactly to proceed.';
            }
          },
        });
        if (!confirm.isConfirmed) {
          return;
        }
      }

      const formData = new FormData();
      formData.append("backupZipFile", uploadFile); // ✅ must match backend param

      setLoading(true);
      const response = await axios.post(`${baseUrl}/zip/restore-database`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: token,
        },
      });
      if (response?.status === 200) {
        // ✅ success
        MySwal.fire("Success", response.data, "success");
      }
    } catch (error) {
      if (error.response) {
        const errorMsg =
          error.response?.data || "❌ Error restoring from uploaded file.";
        MySwal.fire("Restore Failed", errorMsg, "error");
      }
    } finally {
      setLoading(false);
      setUploadFile(null);
    }
  };

  return (
    <div>
      <div className="row">
        <div className="col-sm-12">
          <div className="card min-vh-80" style={{ margin: "0 -25px 0 -20px", marginTop: "-115px" }}>
            <div className="card-body">
              <div className="navigateheaders">
                <div
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <i className="fa-solid fa-arrow-left"></i>
                </div>
                <div></div>
                <div
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  <i className="fa-solid fa-xmark"></i>
                </div>
              </div>

              {loading && (
                <div className="outerspinner active">
                  <div className="spinner"></div>
                </div>
              )}

              <h4> Restore </h4>

              {/* ✅ NEW: SaaS-only warning banner, same Activeprofile/SAS check used in LicenceDetails.jsx */}
              {isSaas && (
                <div className="alert alert-danger" role="alert">
                  ⚠️ You are on the <strong>SaaS platform</strong>. Restoring here affects{" "}
                  <strong>ALL companies</strong> at once — every company's data will be reverted
                  to the selected backup's date, not just one company.
                </div>
              )}

              {/* Restore from Server */}
              <div className="mb-5 p-3 mt-2 border rounded">
                <h4>🔹 Restore from Server Backups</h4>
                <p className="text-muted">
                  The system automatically keeps daily backups for the last 7
                  days. Select one from the list below to restore your database.
                </p>
                <div className="row">
                  <div className="col-md-8">
                    <label>Select Backup File</label>
                    <select
                      className="form-control"
                      value={selectedBackup}
                      onChange={(e) => setSelectedBackup(e.target.value)}
                    >
                      <option value="">-- Choose Backup --</option>
                      {backups.map((b, idx) => (
                        <option key={idx} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <button
                      className={`btn w-100 ${isSaas ? "btn-danger" : "btn-primary"}`}
                      onClick={handleServerRestore}
                    >
                      Restore
                    </button>
                  </div>
                </div>
              </div>

              {/* Restore from Upload */}
              <div className="p-3 border rounded">
                <h4>🔹 Restore from Uploaded File</h4>
                <p className="text-muted">
                  You can upload a backup archive file (<strong>.zip</strong>)
                  to restore your system manually. Make sure the file is generated
                  by the Invoice Billing backup tool.
                </p>
                <div className="row">
                  <div className="col-md-8">
                    <label className="form-label">Upload Backup File</label>
                    <input
                      type="file"
                      accept=".zip"
                      className="form-control"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                    />
                  </div>
                  <div className="col-md-4 d-flex align-items-end">
                    <button
                      className={`btn w-100 ${isSaas ? "btn-danger" : "btn-success"}`}
                      onClick={handleUploadRestore}
                    >
                      Restore
                    </button>
                  </div>
                </div>
              </div>

              {/* Info Section */}
              <div className="mt-5 p-3 bg-light rounded">
                <h5>ℹ️ Important Notes</h5>
                <ul>
                  <li>Server keeps only the last 7 daily backups.</li>
                  <li>
                    Upload restore accepts only <code>.zip</code> files created
                    by the system.
                  </li>
                  <li>
                    Restoring will overwrite current database — make
                    sure to back up important data before proceeding.
                  </li>
                  {isSaas && (
                    <li>
                      <strong>SaaS mode:</strong> a restore here reverts every
                      company on the platform together — it is not per-company.
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestorePage;